import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Navbar, Nav, NavDropdown, Dropdown } from 'react-bootstrap';
import { FaUser, FaCog, FaSignOutAlt, FaRobot, FaRandom } from 'react-icons/fa';
import GlobalSearch from '../GlobalSearch';
import Breadcrumbs from '../Breadcrumbs';
import BillingNotificationBell from '../BillingNotificationBell';
import {
  logoutUser,
  setStores,
  setSelectedStore,
  setClients,
  setSelectedClient,
} from '../../redux/actions';
import { selectViewMode } from '../../redux/selectors';
import { logout } from '../../utils/api';
import { getStoresDataCached, getAdminClientsCached } from '../../utils/metadataCache';
import { useScreenshotMode } from '../../context/ScreenshotModeContext';
import './index.scss';

const Header = ({ sbOpen, onToggleSb, sidebarCollapsed }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { stores, selectedStore } = useSelector((state) => state.stores);
  const { clients, selectedClient } = useSelector((state) => state.clients);
  const user = useSelector((state) => state.user);
  const viewMode = useSelector(selectViewMode);
  const {
    enabled: screenshotMode,
    isSuperAdmin,
    toggle: toggleScreenshotMode,
  } = useScreenshotMode();

  const isOwnerView =
    user?.role === 'Admin' || (user?.role === 'Super Admin' && viewMode === 'owner');

  useEffect(() => {
    if (!user?.isAuthenticated) return;
    getStoresDataCached()
      .then((res) => {
        const storeList = res?.stores ?? [];
        dispatch(setStores(storeList));
        if (storeList.length === 0) {
          dispatch(setSelectedStore(null));
          return;
        }
        const currentStillValid =
          selectedStore && storeList.some((s) => s.storeId === selectedStore.storeId);
        if (!currentStillValid) {
          dispatch(setSelectedStore(storeList[0]));
        }
      })
      .catch(() => {
        dispatch(setStores([]));
        dispatch(setSelectedStore(null));
      });
  }, [user?.isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user?.isAuthenticated || !isOwnerView || !selectedStore?.storeId) return;
    const prevClientId = selectedClient?.id ?? null;
    getAdminClientsCached(selectedStore.storeId)
      .then((res) => {
        const clientList = res?.clients ?? [];
        dispatch(setClients(clientList));
        // Clear the selection only when the previously selected client is no
        // longer present in this store's client list. If it is still valid,
        // leave it as-is so the persisted selection survives a page reload.
        if (prevClientId && !clientList.some((c) => c.id === prevClientId)) {
          dispatch(setSelectedClient(null));
        }
      })
      .catch(() => {
        dispatch(setClients([]));
        dispatch(setSelectedClient(null));
      });
  }, [user?.isAuthenticated, isOwnerView, selectedStore?.storeId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOwnerView) {
      dispatch(setSelectedClient(null));
      dispatch(setClients([]));
    }
  }, [isOwnerView]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMyAccount = async () => {
    navigate('/account');
  };

  const handleLogout = async () => {
    const res = await logout();
    if (res) {
      dispatch(logoutUser());
      navigate('/login');
    }
  };

  const isAdminRole = ['Super Admin', 'Admin'].includes(user?.role);
  const showStoreSelector = stores.length > 0;
  const showClientSelector = isOwnerView;

  return (
    <div className="main-header-sticky">
      <Navbar className="main-header justify-content-between py-0">
        <div className="d-flex align-items-center flex-fill">
          <div className={`logo text-center py-2${sidebarCollapsed ? ' logo--collapsed' : ''}`}>
            {sidebarCollapsed ? (
              <img src="/skusavvy-icon.png" alt="SKUSavvy" className="logo-icon" />
            ) : (
              <img src="/skusavvy-logo.png" alt="SKUSavvy" className="logo-img" />
            )}
          </div>
          <div className="header-breadcrumbs-desktop">
            <Breadcrumbs />
          </div>
        </div>
        <Nav className="d-flex align-items-center">
          {showStoreSelector && (
            <Nav.Item className="me-2 header-store-item">
              <Dropdown align="end">
                <Dropdown.Toggle variant="" className="header-store-toggle">
                  <span className="header-store-toggle__label">
                    {selectedStore?.name || 'Select store'}
                  </span>
                </Dropdown.Toggle>
                <Dropdown.Menu className="header-store-menu">
                  {stores.map((s) => (
                    <Dropdown.Item
                      key={s.storeId}
                      as="button"
                      onClick={() => dispatch(setSelectedStore(s))}
                      active={selectedStore?.storeId === s.storeId}>
                      {s.name}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Nav.Item>
          )}
          {showClientSelector && (
            <Nav.Item className="me-2 header-store-item">
              <Dropdown align="end">
                <Dropdown.Toggle variant="" className="header-store-toggle">
                  <span className="header-store-toggle__label">
                    {selectedClient?.name || 'All Clients'}
                  </span>
                </Dropdown.Toggle>
                <Dropdown.Menu className="header-store-menu">
                  <Dropdown.Item
                    as="button"
                    onClick={() => dispatch(setSelectedClient(null))}
                    active={selectedClient === null}>
                    All Clients
                  </Dropdown.Item>
                  {clients.map((c) => (
                    <Dropdown.Item
                      key={c.id}
                      as="button"
                      onClick={() => dispatch(setSelectedClient(c))}
                      active={selectedClient?.id === c.id}>
                      {c.name}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Nav.Item>
          )}
          <Nav.Item>
            <button
              className={`header-icon-btn header-savvybot-btn${sbOpen ? ' header-savvybot-btn--active' : ''}`}
              onClick={onToggleSb}
              aria-label={sbOpen ? 'Close SavvyBot 3PL' : 'Open SavvyBot 3PL'}
              title="SavvyBot 3PL">
              <FaRobot size={18} />
            </button>
          </Nav.Item>
          {isSuperAdmin && (
            <Nav.Item data-screenshot-mode-control>
              <button
                className={`header-icon-btn${screenshotMode ? ' text-warning' : ''}`}
                onClick={toggleScreenshotMode}
                aria-pressed={screenshotMode}
                aria-label={
                  screenshotMode ? 'Turn off screenshot randomization' : 'Randomize data'
                }>
                <FaRandom size={18} />
              </button>
            </Nav.Item>
          )}
          {isAdminRole && (
            <Nav.Item>
              <GlobalSearch portal="admin" storeId={selectedStore?.storeId} />
            </Nav.Item>
          )}
          <Nav.Item>
            <BillingNotificationBell />
          </Nav.Item>
          <NavDropdown title={<FaUser size={18} />} align="end" id="profile-dropdown">
            <NavDropdown.Item onClick={handleMyAccount}>
              <FaCog className="me-2" /> My Account
            </NavDropdown.Item>
            <NavDropdown.Item onClick={handleLogout}>
              <FaSignOutAlt className="me-2" /> Logout
            </NavDropdown.Item>
          </NavDropdown>
        </Nav>
      </Navbar>
      <div className="header-breadcrumb-row">
        <Breadcrumbs />
      </div>
    </div>
  );
};

export default Header;
